import { useMutation, useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { axiosInstance } from '../lib/axios';

function captureSeasonStarted({ leagueId, teamCount }, data) {
    try {
        posthog.capture('multiplayer_season_started', {
            league_id: leagueId,
            fixtures_count: data?.fixturesCount,
            team_count: teamCount
        });
    } catch (error) {
        // Analytics must never turn a successful season start into a user-facing failure.
        console.warn('Could not record multiplayer season-start analytics.', error);
    }
}

export function useCreateMultiplayerLeague() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['multiplayer-create'],
        mutationFn: async (payload) => {
            const response = await axiosInstance.post('/multiplayer/create', payload);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['user-leagues'] });
        }
    });
}

export function useStartMultiplayerSeason() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['multiplayer-start'],
        mutationFn: async ({ leagueId }) => {
            const encodedLeagueId = encodeURIComponent(String(leagueId));
            const response = await axiosInstance.post(`/multiplayer/${encodedLeagueId}/start`);
            return response.data;
        },
        onSuccess: async (data, variables) => {
            const { leagueId } = variables;
            captureSeasonStarted(variables, data);
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['user-leagues'] }),
                queryClient.invalidateQueries({ queryKey: ['league'] }),
                queryClient.invalidateQueries({ queryKey: ['league-fixtures', leagueId] })
            ]);
        }
    });
}
